import React, { useCallback, useEffect } from 'react';
import ContextViewer from '../../components/orderExtraction/ContextViewer';
import ExtractionSidebar from '../../components/orderExtraction/ExtractionSidebar';
import InputView from '../../components/orderExtraction/InputView';
import ReprocessDialog from '../../components/orderExtraction/ReprocessDialog';
import ResultsView from '../../components/orderExtraction/ResultsView';
import { OrderExtractionProvider, useOrderExtraction } from '../../contexts/OrderExtractionContext';
import './AIOrders.css';

const AIOrdersContent = () => {
  const { state, actions } = useOrderExtraction();
  const {
    currentExtraction,
    extractionData,
    selectedProducts,
    showSidebar,
    selectedFileIndex,
    showContextViewer,
    showReprocessDialog,
    isReprocessing,
    isCreatingExtraction,
    isUploadingFiles,
    isProcessing,
    processingStep,
    selectedFiles,
    textInput,
    customerId,
    extractions,
    isPolling
  } = state;

  // Load extraction history on component mount - only once
  useEffect(() => {
    actions.loadExtractionHistory();
  }, []); // Empty dependency array to run only once

  // Manage polling for pending extractions
  useEffect(() => {
    if (currentExtraction) {
      const isPending = currentExtraction.status !== 'ocr_processed';
      
      if (isPending && !isPolling) {
        // Start polling for pending extraction
        actions.startPolling(currentExtraction.id);
      } else if (!isPending && isPolling) {
        // Stop polling if extraction is no longer pending
        actions.stopPolling();
      }
    } else if (isPolling) {
      // Stop polling if no current extraction
      actions.stopPolling();
    }
    
    // Cleanup polling on unmount
    return () => {
      if (isPolling) {
        actions.stopPolling();
      }
    };
  }, [currentExtraction, isPolling, actions]);

  const handleFilesSelected = useCallback((files) => {
    actions.setSelectedFiles(files);
  }, [actions.setSelectedFiles]);

  const handleTextInput = useCallback((text) => {
    actions.setTextInput(text);
  }, [actions.setTextInput]);

  const handleCustomerIdChange = useCallback((id) => {
    actions.setCustomerId(id);
  }, [actions.setCustomerId]);

  const handleCreateExtraction = useCallback(async () => {
    if (selectedFiles.length === 0 && !textInput.trim()) {
      alert('Por favor, selecione arquivos ou digite um texto.');
      return;
    }

    try {
      // If only text input, create a text file in memory
      let filesToUpload = [...selectedFiles];
      if (textInput.trim() && selectedFiles.length === 0) {
        const textBlob = new Blob([textInput], { type: 'text/plain' });
        const textFile = new File([textBlob], 'texto-pedido.txt', { type: 'text/plain' });
        filesToUpload = [textFile];
      }

      await actions.createExtraction(customerId, filesToUpload);
    } catch (error) {
      alert('Erro ao criar extração. Tente novamente.');
    }
  }, [selectedFiles, textInput, customerId, actions.createExtraction]);

  const handleSelectExtraction = useCallback(async (extraction) => {
    try {
      await actions.selectExtraction(extraction);
    } catch (error) {
      alert('Erro ao carregar detalhes da extração.');
    }
  }, [actions.selectExtraction]);

  const handleCreateNewExtraction = useCallback(() => {
    // Stop any ongoing polling
    actions.stopPolling();
    actions.setCurrentExtraction(null);
    actions.setExtractionData(null);
    actions.clearForm();
    actions.clearSelectedProducts();
    actions.setSidebarVisibility(true);
  }, [actions]);

  const handleShowHistory = useCallback(() => {
    actions.setSidebarVisibility(true);
  }, [actions.setSidebarVisibility]);

  const handleShowContext = useCallback(() => {
    actions.setContextViewer(true);
  }, [actions.setContextViewer]);

  const handleShowReprocess = useCallback(() => {
    actions.setReprocessDialog(true);
  }, [actions.setReprocessDialog]);

  const handleFileSelect = useCallback((file, index) => {
    actions.setSelectedFileIndex(index);
  }, [actions.setSelectedFileIndex]);

  const handleRemoveSelection = useCallback((itemIndex) => {
    actions.removeSelectedProduct(itemIndex);
  }, [actions.removeSelectedProduct]);

  const handleClearAllSelections = useCallback(() => {
    actions.clearSelectedProducts();
  }, [actions.clearSelectedProducts]);

  const handleSubmitOrder = useCallback(() => {
    // TODO: Implement order submission to ERP
    alert('Funcionalidade de envio para ERP será implementada em breve!');
  }, []);

  const handleReprocess = useCallback(async (additionalContext) => {
    if (!currentExtraction) return;
    
    try {
      await actions.reprocessExtraction(currentExtraction.id, additionalContext);
    } catch (error) {
      alert('Erro ao reprocessar pedido.');
    }
  }, [currentExtraction, actions.reprocessExtraction]);

  const formatTimestamp = useCallback((timestamp) => {
    return new Date(timestamp).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }, []);

  const getStatusLabel = useCallback((status) => {
    switch (status) {
      case 'ocr_processed':
        return 'Processado';
      case 'ocr_pending':
        return 'Pendente';
      default:
        return 'Pendente';
    }
  }, []);

  const getStatusClass = useCallback((status) => {
    switch (status) {
      case 'ocr_processed':
        return 'success';
      case 'ocr_pending':
        return 'pending';
      default:
        return 'pending';
    }
  }, []);

  return (
    <div className="ai-orders">
      {/* Sidebar - Extraction History */}
      {showSidebar && (
        <ExtractionSidebar
          extractions={extractions}
          currentExtractionId={currentExtraction?.id}
          onSelectExtraction={handleSelectExtraction}
          onCreateNewExtraction={handleCreateNewExtraction}
          formatTimestamp={formatTimestamp}
          getStatusLabel={getStatusLabel}
          getStatusClass={getStatusClass}
          onClose={() => actions.setSidebarVisibility(!showSidebar)}
        />
      )}

        {currentExtraction ? (
          /* Results View - Three Column Layout */
          <ResultsView
            extractionData={extractionData}
            selectedFileIndex={selectedFileIndex}
            onFileSelect={handleFileSelect}
            selectedProducts={selectedProducts}
            onRemoveSelection={handleRemoveSelection}
            onClearAllSelections={handleClearAllSelections}
            onSubmitOrder={handleSubmitOrder}
            currentExtraction={currentExtraction}
            handleShowHistory={handleShowHistory}
            handleShowContext={handleShowContext}
            handleShowReprocess={handleShowReprocess}
            isReprocessing={isReprocessing}
            getStatusLabel={getStatusLabel}
            showSidebar={showSidebar}
            onNewExtraction={handleCreateNewExtraction}
          />
        ) : (
          /* Input View */
          <InputView
            customerId={customerId}
            onCustomerIdChange={handleCustomerIdChange}
            onFilesSelected={handleFilesSelected}
            onTextInput={handleTextInput}
            onCreateExtraction={handleCreateExtraction}
            isCreatingExtraction={isCreatingExtraction}
            isUploadingFiles={isUploadingFiles}
            isProcessing={isProcessing}
            processingStep={processingStep}
            selectedFiles={selectedFiles}
            textInput={textInput}
            disabled={isCreatingExtraction || isUploadingFiles || isProcessing}
          />
        )}

      {/* Context Viewer Dialog */}
      <ContextViewer
        contextItems={extractionData?.contextItems}
        isOpen={showContextViewer}
        onClose={useCallback(() => actions.setContextViewer(false), [actions.setContextViewer])}
      />

      {/* Reprocess Dialog */}
      <ReprocessDialog
        isOpen={showReprocessDialog}
        onClose={useCallback(() => actions.setReprocessDialog(false), [actions.setReprocessDialog])}
        onReprocess={handleReprocess}
        isProcessing={isReprocessing}
      />
    </div>
  );
};

const AIOrders = () => {
  return (
    <OrderExtractionProvider>
      <AIOrdersContent />
    </OrderExtractionProvider>
  );
};

export default AIOrders;